import { KalturaPurchaseStatus } from '@kaltura-ott/ott-client-types';
import {
  FormatKalturaProductPricePpv,
  getUserRules,
  KuxError,
  KuxMediaAssetType,
  Provider,
  ProviderLinkNotFound,
} from '@kaltura-ott/tvpil';
import { computed, observable } from 'mobx';
import { lastValueFrom } from 'rxjs';
import { container } from 'tsyringe';

import { formatStringForDate } from '../../utils/date';
import { errorHandling } from '../../utils/error-handling';
import { ErrorPriority } from '../error';
import { ProvidersRepository } from '../providersRepository/providers-repository';

import { PlayableAsset } from './PlayableAsset';
import type { Ppv } from './Ppv';

export abstract class VodAsset<T extends KuxMediaAssetType = KuxMediaAssetType> extends PlayableAsset<T> {
  @observable rents: Ppv[] = [];
  @observable provider?: string;
  @observable _isPurchasableForceAttribute?: boolean;
  /**
   * used to display the badge in the rental rail
   */
  @observable _rentedForceAttribute?: boolean;

  private providersRepository?: ProvidersRepository;

  constructor(data: T) {
    super(data);
    this.providersRepository = container.resolve<ProvidersRepository>('providersRepository');
    this.provider = this.providersRepository.calculateAssetProviders(this.data.metas);
  }

  async setupRules(): Promise<void> {
    this.rules = await lastValueFrom(getUserRules(this.data.id, 1));
  }

  /**
   * detect if VOD has any rent plan purchased
   */

  @computed get isRented(): boolean {
    if (!this.price) {
      return !!this._rentedForceAttribute;
    }
    return this.price?.purchaseStatus === KalturaPurchaseStatus.ppvPurchased;
  }

  setPrice(priceValue: FormatKalturaProductPricePpv | undefined) {
    if (!priceValue) {
      return;
    }
    this.price = priceValue;
  }

  setRentalAttribute(value: boolean) {
    this._rentedForceAttribute = value;
    this.price = undefined;
  }

  @computed get isPlayable(): boolean {
    return true;
  }

  @computed get isPurchased(): boolean | undefined {
    if (this.isProviderAsset) {
      return true;
    }

    if (!this.price) {
      return undefined;
    }
    return ![
      KalturaPurchaseStatus.forPurchase,
      KalturaPurchaseStatus.forPurchaseSubscriptionOnly,
      KalturaPurchaseStatus.pendingEntitlement,
    ].includes(this.price.purchaseStatus);
  }

  setPurchasableAttribute(value: boolean) {
    this._isPurchasableForceAttribute = value;
  }

  @computed get isPurchasable() {
    if (this._isPurchasableForceAttribute !== undefined) {
      return this._isPurchasableForceAttribute;
    }
    let hasValidBilling = false;
    const billings = this.data?.tags['BillingId'];
    if (billings?.length) {
      billings.forEach((billing) => {
        const [, startDateRaw, endDateRaw] = billing.split(';');

        if (!startDateRaw || !endDateRaw) {
          return;
        }

        const startDate = new Date(formatStringForDate(startDateRaw)).getTime();
        const endDate = new Date(formatStringForDate(endDateRaw)).getTime();
        const currentDate = new Date().getTime();

        if (startDate < currentDate && currentDate < endDate) {
          hasValidBilling = true;
          return;
        }
      });
    }
    return hasValidBilling;
  }

  /**
   * detect if VOD has any rent plans available
   */

  @computed get isTvod(): boolean {
    return this.rents.length > 0;
  }

  @computed get isSvod(): boolean {
    return !!this.selectedSubscription;
  }

  /**
   * detect if asset has one rent plan available
   */

  @computed get isSingleRentAvailable(): boolean {
    return this.rents.length === 1;
  }

  /**
   * detect if asset has more than one rent plan available
   */
  @computed get isMultipleRentsAvailable(): boolean {
    return this.rents.length > 1;
  }

  @computed get isProviderAsset(): boolean {
    return !!this.provider;
  }

  @computed get providerLogo(): string | undefined {
    return this.providersRepository?.providerAssetLogo(this.provider);
  }

  @computed get providerSettings(): Provider | undefined {
    if (this.provider) {
      return this.providersRepository?.providerSettings[this.provider];
    }
  }

  @computed get isDeviceAllowedDeepLink(): boolean | undefined {
    return this.providersRepository?.isDeviceAllowedDeepLink(this.provider);
  }

  @computed get isDeviceAllowedGoDirectlyToStore(): boolean | undefined {
    return this.providersRepository?.isDeviceAllowedGoDirectlyToStore(this.provider);
  }

  @computed get isAssetPlayOnExternalApp(): boolean | undefined {
    return this.providersRepository?.isAssetPlayOnExternalApp(this);
  }

  @errorHandling({ type: ErrorPriority.medium })
  watch(): string | undefined {
    if (!this.isProviderAsset || !this.isAssetPlayOnExternalApp) {
      return undefined;
    }

    if (this.data.metas?.Deeplink) {
      return this.data.metas?.Deeplink?.toString();
    } else {
      throw new KuxError(ProviderLinkNotFound);
    }
  }
}
